// libraries
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";

// textAreaStyles
import textAreaStyles from "./textArea.module.scss";

export enum InputType {
  DEFAULT = "DEFAULT",
  NUMBER = "NUMBER",
}

interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  defaultValue?: string | number;
  maxCharacters?: number;
  // eslint-disable-next-line no-unused-vars
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  type?: InputType;
  description?: string;
  required?: boolean;
  errorFormat?: boolean;
  errorMessage?: string;
  height?: string;
  textRows?: number;
  rangeSelector?: [number, number];
}

const TextArea: React.FC<Props> = ({
  label = "",
  defaultValue = "",
  maxCharacters = 100,
  handleChange,
  description,
  required,
  errorFormat = false,
  errorMessage,
  type = InputType.DEFAULT,
  height,
  textRows = 1,
  rangeSelector,
  ...props
}: Props) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState<string | number>("");
  const [error, setError] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const rows = Math.ceil(maxCharacters / 100);
  const errorClass = error ? "error" : "";

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      if (e?.target?.value?.length > maxCharacters) {
        setError(true);
        setValue(e.target.value);
      } else {
        setError(false);
        setValue(e.target.value);
        handleChange(e);
      }
    },
    [handleChange, maxCharacters]
  );

  useEffect(() => {
    if (required && !value && focused) {
      setError(true);
    }
  }, [focused, required, value]);

  useEffect(() => {
    setError(errorFormat);
  }, [errorFormat]);

  const validateNumber = (
    value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
  ) => {
    if (/[٠-٩]/.test(value)) return true;
    if (/[0-9]/.test(value)) return true;
    if (value === ".") return true;
    return false;
  };

  const incrementRange = useCallback(() => {
    if (rangeSelector && Number(inputRef?.current?.value) < rangeSelector[1]) {
      try {
        if (inputRef.current) {
          inputRef.current.value = `${Number(inputRef.current.value) + 1}`;
          inputRef.current.dispatchEvent(
            new Event("textarea", { bubbles: true })
          );
          const event = new Event("change");
          inputRef?.current?.dispatchEvent(event);
          onChange(event as unknown as React.ChangeEvent<HTMLTextAreaElement>);
        }
      } catch (error) {
        console.error({ error });
      }
    }
  }, [onChange, rangeSelector]);

  const decrementRange = useCallback(() => {
    if (rangeSelector && Number(inputRef?.current?.value) > rangeSelector[0]) {
      try {
        if (inputRef.current) {
          inputRef.current.value = `${Number(inputRef.current.value) - 1}`;
          inputRef.current.dispatchEvent(
            new Event("textarea", { bubbles: true })
          );
          const event = new Event("change");
          inputRef?.current?.dispatchEvent(event);
          onChange(event as unknown as React.ChangeEvent<HTMLTextAreaElement>);
        }
      } catch (error) {
        console.error({ error });
      }
    }
  }, [onChange, rangeSelector]);

  return (
    <div className={textAreaStyles["outerContainer"]}>
      <div
        style={{ height: height }}
        className={classNames(
          textAreaStyles["container"],
          textAreaStyles[`${errorClass}`]
        )}>
        <div className={"header mb1"}>
          <p className={"hint"}>{label}</p>
        </div>
        <textarea
          ref={inputRef}
          rows={textRows || rows}
          className={classNames(textAreaStyles["text_area"])}
          maxLength={maxCharacters}
          aria-label="maximum height"
          defaultValue={defaultValue}
          onChange={onChange}
          style={{ minHeight: !height ? `${rows - 0.5}rem` : `${rows - 1}rem` }}
          onKeyPress={(event) => {
            if (
              (event?.charCode == 13 || event?.code == "Enter") &&
              rows <= 1
            ) {
              event.preventDefault();
            }
            if (!validateNumber(event?.key) && type === InputType.NUMBER) {
              event.preventDefault();
            }
          }}
          inputMode={type === InputType.NUMBER ? "decimal" : "text"}
          onFocus={() => {
            setFocused(true);
          }}
          onPaste={(event) => {
            if (type === InputType.NUMBER) {
              event.preventDefault();
            }
          }}
          {...props}
        />
        {rangeSelector ? (
          <div className={textAreaStyles["range_selector"]}>
            <div onClick={incrementRange}>
              <img src="/assets/images/svg/up.svg" alt="up" />
            </div>
            <div onClick={decrementRange}>
              <img src="/assets/images/svg/down.svg" alt="down" />
            </div>
          </div>
        ) : (
          <div className={textAreaStyles["text_length"]}>
            {description ? (
              <p>{description}</p>
            ) : (
              <p
                className={
                  maxCharacters < String(value)?.length
                    ? textAreaStyles["error_text"]
                    : textAreaStyles[""]
                }>
                {maxCharacters}
                <span>/{String(value || defaultValue)?.length}</span>
              </p>
            )}
          </div>
        )}
      </div>
      <div className={textAreaStyles["error_container"]}>
        {errorMessage && (
          <p dir={"rlt"} className={textAreaStyles["error_text"]}>
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(TextArea);
